import {
  APIResponse,
  GETRecentlyOpenedEntitiesResponse,
  IOrderAccountData,
  RecentlyOpenedEntity,
  RecentlyOpenedEntityType,
  ResultObjectType
} from '@cbgms/api'
import { useMemo } from 'react'
import { UseQueryResult } from '@tanstack/react-query'
import { usePrimaryActionBuilder } from 'app/menu/components/Search/hooks/use-primary-action-builder'
import { ITopSearchResultProps } from '@cbgms/components/src/top-search/search-v1/Results/ResultComponentSelector'
import { useSelector } from 'react-redux'
import { IRootReducerState } from 'state/rootReducer'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { useFeature } from '@cbgms/base/feature-flags'

export const useFormatRecentlyOpenedResults = (
  query: UseQueryResult<APIResponse<RecentlyOpenedEntity[]> | GETRecentlyOpenedEntitiesResponse, unknown>,
  clearQuery: () => void,
  setQuery?: (value: string) => void,
  refetchSearch?: () => void,
  inputRef?: React.RefObject<HTMLInputElement>
): ITopSearchResultProps[] => {
  const orderAccountDetails = useSelector<IRootReducerState, IOrderAccountData | undefined>(
    state => state.settings.defaultOrderAccount.details
  )
  const orderSystem = orderAccountDetails?.Wholesaler?.OrderSystem
  const isPortal = useIsPortal()
  const hasPreviouslySearchedVehicle = useFeature('b2b-fr0103-previously-searched-vehicle')

  const actionBuilder = usePrimaryActionBuilder({ clearQuery, setQuery, refetchSearch, inputRef })

  return useMemo(() => {
    if (query.isFetching) {
      return []
    }
    const data: RecentlyOpenedEntity[] = query.data && Array.isArray(query.data.Data) ? query.data.Data : []
    const hasInvoices = !!data?.find((val: RecentlyOpenedEntity) => val.EntityType === RecentlyOpenedEntityType.WorkorderInvoice)
    const result = data?.map((value: RecentlyOpenedEntity) => {
      value['hasInvoices'] = hasInvoices
      const title =
        value.EntityType === RecentlyOpenedEntityType.WorkorderInvoice
          ? 'RecentlyOpened.WorkorderInvoice'
          : value.EntityType === RecentlyOpenedEntityType.Workorder
          ? 'RecentlyOpened.Workorders'
          : value.EntityType === RecentlyOpenedEntityType.Vehicle
          ? 'RecentlyOpened.Vehicle'
          : 'RecentlyOpened.Orders'

      return {
        Score: 1,
        Type: ResultObjectType.RecentlyOpened,
        Title: title,
        query: '',
        clearQuery,
        primaryAction: actionBuilder(ResultObjectType.RecentlyOpened, '', value),
        RecentlyOpened: value,
        hasGroupHeader: false
      }
    })

    const orders = result.filter(
      (val: ITopSearchResultProps) => val.RecentlyOpened && val.RecentlyOpened.EntityType === RecentlyOpenedEntityType.Order
    )
    const workOrders = result.filter(
      (val: ITopSearchResultProps) => val.RecentlyOpened && val.RecentlyOpened.EntityType === RecentlyOpenedEntityType.Workorder
    )
    const invoices = result.filter(
      (val: ITopSearchResultProps) => val.RecentlyOpened && val.RecentlyOpened.EntityType === RecentlyOpenedEntityType.WorkorderInvoice
    )
    const vehicles = result.filter((val: any) => val.RecentlyOpened && val.RecentlyOpened.EntityType === RecentlyOpenedEntityType.Vehicle)
    if (isPortal && hasPreviouslySearchedVehicle) {
      return [...vehicles]
    } else {
      return [...orders, ...workOrders, ...invoices]
    }
  }, [query.data?.Data, query.isFetching, clearQuery, orderSystem])
}
